import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
// import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import { examService } from '../../services/examService';
import { useAuth } from '../../context/AuthContext';
import { 
  Calendar, 
  Clock, 
  User, 
  BookOpen, 
  Play, 
  CheckCircle,
  ArrowLeft,
  Save,
  Send,
  Timer,
  Eye,
  FileText
} from 'lucide-react';

const ExamDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') || 'view'; // 'view', 'take', 'result'
  const isStaff = user?.role === 'staff';
  
  const [exam, setExam] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [examStarted, setExamStarted] = useState(false);
  const [result, setResult] = useState(null);

  const fetchExam = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await examService.getExam(id);
      const examData = response.data.data;
      setExam(examData);
      
      // If taking exam or staff viewing, get questions
      // For staff, questions may already be included in exam data
      if (mode === 'take' || (mode === 'view' && isStaff)) {
        // Check if questions are already in exam data
        if (examData.questions && examData.questions.length > 0) {
          setQuestions(examData.questions);
        } else {
          // Fallback: try to fetch questions separately
          try {
            const questionsResponse = await examService.getExamQuestions(id);
            setQuestions(questionsResponse.data.data || []);
          } catch (questionsError) {
            console.error('Questions fetch error:', questionsError);
            // For staff, questions might not be available, that's okay
            if (mode === 'take') {
              setError('Failed to load exam questions');
            }
          }
        }
        
        // Get any existing progress (only for students taking exam)
        if (mode === 'take') {
          try {
            const progressResponse = await examService.getExamProgress(id);
            if (progressResponse.data.data) {
              setAnswers(progressResponse.data.data.answers || {});
              setExamStarted(true);
            }
          } catch (progressError) {
            // No existing progress, that's okay
            console.log('No existing progress found');
          }
        }
      }
      
      // If viewing result, get result
      if (mode === 'result') {
        try {
          const resultResponse = await examService.getExamResult(id);
          setResult(resultResponse.data.data);
        } catch (resultError) {
          console.error('Result fetch error:', resultError);
          setError('Failed to load exam result');
        }
      }
      
    } catch (err) {
      console.error('Exam fetch error:', err);
      setError(err.response?.data?.message || 'Failed to load exam');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchExam();
    }
  }, [id, mode]);

  // Timer effect for exam taking
  useEffect(() => {
    if (mode === 'take' && exam && examStarted && !submitting) {
      const startTime = new Date(exam.startTime);
      const duration = exam.duration * 60 * 1000; // Convert minutes to milliseconds
      const endTime = new Date(startTime.getTime() + duration);
      
      const timer = setInterval(() => {
        const now = new Date();
        const remaining = endTime - now;
        
        if (remaining <= 0) {
          setTimeLeft(0);
          handleSubmitExam(); // Auto-submit when time runs out
          clearInterval(timer);
        } else {
          setTimeLeft(remaining);
        }
      }, 1000);
      
      return () => clearInterval(timer);
    }
  }, [exam, examStarted, submitting]);

  const startExam = async () => {
    try {
      setSubmitting(true);
      await examService.submitExamAnswers(id, { action: 'start' });
      setExamStarted(true);
    } catch (err) {
      console.error('Start exam error:', err);
      setError('Failed to start exam');
    } finally {
      setSubmitting(false);
    }
  };

  const saveProgress = async () => {
    try {
      await examService.saveExamProgress(id, { answers });
    } catch (err) {
      console.error('Save progress error:', err);
    }
  };

  const handleSubmitExam = async () => {
    try {
      setSubmitting(true);
      await examService.submitExamAnswers(id, { answers });
      
      // Redirect to result page
      navigate(`/dashboard/exams/results/${id}`);
    } catch (err) {
      console.error('Submit exam error:', err);
      setError('Failed to submit exam');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (milliseconds) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleAnswerChange = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
    
    // Auto-save progress
    setTimeout(() => {
      saveProgress();
    }, 1000);
  };

  if (loading) return <LoadingSpinner />;
  
  if (error) return (
    <div className="p-6">
      <Card>
        <CardContent className="p-6">
          <div className="text-center">
            <div className="text-red-600 text-sm mb-4">{error}</div>
            <Button onClick={fetchExam}>Retry</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  if (!exam) return <div>Exam not found</div>;

  const examDate = new Date(exam.examDate);
  const startTime = new Date(`${exam.examDate}T${exam.startTime}`);
  const canTakeExam = exam.status === 'ongoing' && new Date() >= startTime;
  // const isOverdue = new Date() > new Date(startTime.getTime() + exam.duration * 60 * 1000);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => window.history.back()}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{exam.title}</h2>
            <p className="text-gray-600 mt-1">
              {mode === 'take' ? 'Taking Exam' : 
               mode === 'result' ? 'Exam Result' : 
               'Exam Details'}
            </p>
          </div>
        </div>
        
        {/* Timer for exam taking */}
        {mode === 'take' && examStarted && timeLeft !== null && (
          <div className="flex items-center space-x-2">
            <Timer className="h-5 w-5 text-red-600" />
            <Badge variant={timeLeft < 300000 ? 'destructive' : 'secondary'}>
              {formatTime(timeLeft)}
            </Badge>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Exam Taking Mode */}
          {mode === 'take' && (
            <>
              {!examStarted ? (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Play className="h-5 w-5" />
                      <span>Ready to Start?</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded">
                      <h3 className="font-semibold text-blue-800 mb-2">Exam Instructions:</h3>
                      <ul className="text-blue-700 space-y-1">
                        <li>• You have {exam.duration} minutes to complete this exam</li>
                        <li>• Answer all questions to the best of your ability</li>
                        <li>• Your progress will be saved automatically</li>
                        <li>• Once you start, the timer cannot be paused</li>
                        <li>• Submit your answers before time runs out</li>
                      </ul>
                    </div>
                    
                    <div className="flex justify-end space-x-2">
                      <Button 
                        variant="outline" 
                        onClick={() => window.history.back()}
                      >
                        Cancel
                      </Button>
                      <Button 
                        onClick={startExam}
                        disabled={submitting || !canTakeExam}
                      >
                        {submitting ? (
                          <>
                            <LoadingSpinner className="h-4 w-4 mr-2" />
                            Starting...
                          </>
                        ) : (
                          <>
                            <Play className="h-4 w-4 mr-2" />
                            Start Exam
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span>Exam Questions</span>
                      <div className="flex items-center space-x-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={saveProgress}
                        >
                          <Save className="h-4 w-4 mr-1" />
                          Save Progress
                        </Button>
                        <Button 
                          size="sm"
                          onClick={handleSubmitExam}
                          disabled={submitting}
                        >
                          {submitting ? (
                            <>
                              <LoadingSpinner className="h-4 w-4 mr-1" />
                              Submitting...
                            </>
                          ) : (
                            <>
                              <Send className="h-4 w-4 mr-1" />
                              Submit Exam
                            </>
                          )}
                        </Button>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {questions.map((question, index) => (
                      <div key={question.id} className="p-4 border rounded-lg">
                        <div className="mb-4">
                          <h3 className="font-semibold text-lg">
                            Question {index + 1}
                          </h3>
                          <p className="text-gray-700 mt-2">{question.question}</p>
                        </div>
                        
                        {question.type === 'multiple_choice' && (
                          <div className="space-y-2">
                            {question.options.map((option, optionIndex) => (
                              <div key={optionIndex} className="flex items-center space-x-2">
                                <input
                                  type="radio"
                                  id={`${question.id}-${optionIndex}`}
                                  name={`question-${question.id}`}
                                  value={option}
                                  checked={answers[question.id] === option}
                                  onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                                  className="rounded"
                                />
                                <Label htmlFor={`${question.id}-${optionIndex}`}>{option}</Label>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {question.type === 'essay' && (
                          <Textarea
                            placeholder="Write your answer here..."
                            value={answers[question.id] || ''}
                            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                            rows={4}
                          />
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* Exam Result Mode */}
          {mode === 'result' && result && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span>Exam Result</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-green-50 border border-green-200 rounded">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-green-800">Score: {result.score}/{result.totalPoints}</h3>
                      <p className="text-green-700">Grade: {result.grade}</p>
                    </div>
                    <Badge variant="outline" className="text-green-800">
                      {result.percentage}%
                    </Badge>
                  </div>
                </div>
                
                {result.feedback && (
                  <div>
                    <h4 className="font-medium mb-2">Teacher Feedback:</h4>
                    <p className="text-gray-700">{result.feedback}</p>
                  </div>
                )}
                
                <div>
                  <h4 className="font-medium mb-2">Answer Review:</h4>
                  <div className="space-y-3">
                    {result.questionResults?.map((qResult, index) => (
                      <div key={index} className="p-3 border rounded">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium">Question {index + 1}</span>
                          <Badge variant={qResult.correct ? 'default' : 'destructive'}>
                            {qResult.correct ? 'Correct' : 'Incorrect'}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600 mb-1">Your answer: {qResult.studentAnswer}</p>
                        {!qResult.correct && (
                          <p className="text-sm text-gray-600">Correct answer: {qResult.correctAnswer}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Exam Details Mode */}
          {mode === 'view' && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <BookOpen className="h-5 w-5" />
                    <span>Exam Details</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-lg mb-2">{exam.title}</h3>
                    <p className="text-gray-700">{exam.description}</p>
                  </div>
                  
                  {exam.instructions && (
                    <div>
                      <h4 className="font-medium mb-2">Instructions:</h4>
                      <p className="text-gray-700 whitespace-pre-wrap">{exam.instructions}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Show questions for staff in view mode */}
              {isStaff && questions.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <FileText className="h-5 w-5" />
                      <span>Exam Questions ({questions.length})</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {questions.map((question, index) => (
                      <div key={question.id} className="p-4 border rounded-lg">
                        <div className="mb-4">
                          <h3 className="font-semibold text-lg">
                            Question {index + 1}
                            {question.points && (
                              <span className="text-sm text-gray-500 ml-2">({question.points} points)</span>
                            )}
                          </h3>
                          <p className="text-gray-700 mt-2">{question.question}</p>
                        </div>
                        
                        {question.type === 'multiple_choice' && question.options && (
                          <div className="space-y-2">
                            {question.options.map((option, optionIndex) => (
                              <div key={optionIndex} className="flex items-center space-x-2">
                                <input
                                  type="radio"
                                  disabled
                                  checked={question.correctAnswer === option}
                                  className="rounded"
                                />
                                <Label className={question.correctAnswer === option ? 'font-semibold text-green-700' : ''}>
                                  {option}
                                  {question.correctAnswer === option && (
                                    <Badge className="ml-2 bg-green-100 text-green-800">Correct</Badge>
                                  )}
                                </Label>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {question.type === 'essay' && (
                          <div className="p-3 bg-gray-50 border rounded">
                            <p className="text-sm text-gray-600">Essay question - requires manual grading</p>
                            {question.correctAnswer && (
                              <div className="mt-2">
                                <p className="text-sm font-medium">Sample Answer:</p>
                                <p className="text-sm text-gray-700">{question.correctAnswer}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>

        {/* Exam Info Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Exam Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Calendar className="h-4 w-4 text-gray-500" />
                <div>
                  <div className="text-sm text-gray-500">Date</div>
                  <div className="font-medium">{examDate.toLocaleDateString()}</div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Clock className="h-4 w-4 text-gray-500" />
                <div>
                  <div className="text-sm text-gray-500">Start Time</div>
                  <div className="font-medium">{exam.startTime}</div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Timer className="h-4 w-4 text-gray-500" />
                <div>
                  <div className="text-sm text-gray-500">Duration</div>
                  <div className="font-medium">{exam.duration} minutes</div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <BookOpen className="h-4 w-4 text-gray-500" />
                <div>
                  <div className="text-sm text-gray-500">Subject</div>
                  <div className="font-medium">{exam.subjectName || 'N/A'}</div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <User className="h-4 w-4 text-gray-500" />
                <div>
                  <div className="text-sm text-gray-500">Teacher</div>
                  <div className="font-medium">{exam.teacherName || 'N/A'}</div>
                </div>
              </div>

              {exam.totalQuestions && (
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-gray-500" />
                  <div>
                    <div className="text-sm text-gray-500">Questions</div>
                    <div className="font-medium">{exam.totalQuestions}</div>
                  </div>
                </div>
              )}

              {exam.totalPoints && (
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-gray-500" />
                  <div>
                    <div className="text-sm text-gray-500">Total Points</div>
                    <div className="font-medium">{exam.totalPoints}</div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <Card>
            <CardContent className="p-4">
              <div className="space-y-2">
                {mode === 'view' && canTakeExam && (
                  <Button 
                    className="w-full" 
                    onClick={() => navigate(`/dashboard/exams/take/${id}`)}
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Take Exam
                  </Button>
                )}
                
                {mode === 'take' && examStarted && (
                  <Button 
                    className="w-full"
                    onClick={handleSubmitExam}
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <LoadingSpinner className="h-4 w-4 mr-2" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Submit Exam
                      </>
                    )}
                  </Button>
                )}
                
                {mode === 'result' && (
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => navigate(`/dashboard/exams/${id}`)}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    View Exam Details
                  </Button>
                )}
                
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => window.history.back()}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Exams
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ExamDetail;